import clsx from 'clsx'
import { useMemo, useEffect, useState } from 'preact/hooks'
import { render } from 'preact'
import Icon from '@/components/snippets/icon/icon'
import { useSwatches } from '@/components/hooks/useSwatches'
import { getLowQualityPlaceholder, generateSrcSet } from './ResultImage'

const SWATCH_MODAL_ID = 'quick-add-modal'
const IMAGE_WIDTHS = [320, 480, 560, 640, 720, 960]

const productImageCache = new Map()

function toImageUrl(val) {
  if (val == null) return null
  if (typeof val === 'string' && val.length > 0) return val
  const url = typeof val === 'object' ? val.url ?? val.src : null
  return url != null ? String(url) : null
}

async function fetchProductImage(handle) {
  if (!handle) return null
  if (productImageCache.has(handle)) return productImageCache.get(handle)

  try {
    const res = await fetch(`/products/${handle}.js`)
    if (!res.ok) return null
    const data = await res.json()
    const imageUrl = data.featured_image || data.images?.[0] || null
    productImageCache.set(handle, imageUrl)
    return imageUrl
  } catch {
    return null
  }
}

const MARKET_OOS_TAG = window.globals?.localization?.market
  ? `Flow:OutOfStock:${window.globals.localization.market}`
  : null

function isSwatchOutOfStock(swatch) {
  if (swatch.tags?.includes('Flow:OutOfStock')) return true
  if (MARKET_OOS_TAG && swatch.tags?.includes(MARKET_OOS_TAG)) return true
  return false
}

function getSwatchBgColour(swatch) {
  const tag = swatch?.tags?.find((t) => t.includes('Hex'))
  const colour = tag ? tag.split('-').pop() : 'var(--colour-accent)'
  return swatch?.background_colour || colour
}

const SwatchModalContent = ({ swatches, onClose }) => {
  const filteredSwatches = useMemo(
    () => (swatches || []).filter((swatch) => !isSwatchOutOfStock(swatch)),
    [swatches]
  )
  const [fetchedImages, setFetchedImages] = useState({})

  const handlesKey = useMemo(
    () => filteredSwatches
      .filter((s) => !toImageUrl(s.image) && !toImageUrl(s.featured_image) && s.handle)
      .map((s) => s.handle)
      .join(','),
    [filteredSwatches]
  )

  useEffect(() => {
    const handles = handlesKey ? handlesKey.split(',') : []
    if (handles.length === 0) return

    let cancelled = false

    Promise.all(
      handles.map(async (handle) => {
        const url = await fetchProductImage(handle)
        return [handle, url]
      })
    ).then((entries) => {
      if (cancelled) return
      const map = {}
      for (const [handle, url] of entries) {
        if (url) map[handle] = url
      }
      setFetchedImages(map)
    })

    return () => { cancelled = true }
  }, [handlesKey])

  useEffect(() => {
    if (window.lazyLoadInstance) {
      setTimeout(() => window.lazyLoadInstance.update(), 0)
    }
  }, [swatches, fetchedImages])

  const count = filteredSwatches.length
  const gridClass = count < 4 ? 'grid-cols-2' : count >= 10 ? 'grid-cols-3 md:grid-cols-4' : 'grid-cols-3'

  return (
    <>
      <div className="sticky z-20 flex h-[60px] w-full shrink-0 items-center justify-between gap-x-1 border-b border-neutral-medium bg-secondary px-margin inset-tl md:h-[84px]">
        <h2 className="h4">
          View All {count} Colour{count === 1 ? '' : 's'}
        </h2>
        <button
          className="size-[14px] link-fade children:size-full"
          data-modal-parent={SWATCH_MODAL_ID}
          data-modal-role="close"
          aria-label="Close colours modal"
          onClick={onClose}
        >
          <Icon id="close" />
        </button>
      </div>
      <div className="flex grow flex-col gap-y-margin overflow-y-auto overscroll-none p-margin">
        <p className="paragraph-b text-neutral-secondary">Tap colour to view product</p>
        <div className={clsx('grid gap-0.5 md:gap-0.75', gridClass)}>
          {filteredSwatches.map((swatch) => {
            const imageSrc =
              toImageUrl(swatch.image) ??
              toImageUrl(swatch.featured_image) ??
              fetchedImages[swatch.handle] ??
              null
            const title = swatch.colourName || ''
            return (
              <a
                key={swatch.handle}
                href={swatch.url}
                className="group/swatch-card overflow-hidden flex flex-col rounded border border-neutral-light bg-neutral-light"
                aria-label={title ? `View product in ${title}` : 'View product'}
                data-dl-track="select_item"
                data-dl-product-id={swatch.productLegacyResourceId}
                data-dl-attribute="item_colour"
                data-dl-value={swatch.colourName}
              >
                <div
                  className="overflow-hidden relative aspect-3/4 w-full bg-[--bg-colour] children:size-full children:object-cover"
                  style={{ '--bg-colour': getSwatchBgColour(swatch) }}
                >
                  {imageSrc && (
                    <img
                      className="lazy transition-all group-hover/swatch-card:scale-[1.02]"
                      src={getLowQualityPlaceholder(imageSrc)}
                      data-src={imageSrc}
                      data-srcset={generateSrcSet(imageSrc, IMAGE_WIDTHS)}
                      data-sizes="(max-width: 640px) 50vw, 33vw"
                      alt={title || 'Product'}
                    />
                  )}
                </div>
              </a>
            )
          })}
        </div>
      </div>
    </>
  )
}

function openSwatchModal(swatches) {
  const container = document.querySelector('[data-quick-add-modal-inner]')
  if (!container) return
  const modalId = SWATCH_MODAL_ID
  const closeModal = () => {
    const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
    if (modal && typeof modal.handleHide === 'function') {
      modal.handleHide()
    }
  }
  render(<SwatchModalContent swatches={swatches} onClose={closeModal} />, container)
  setTimeout(() => {
    const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
    if (modal && typeof modal.handleToggle === 'function') {
      modal.handleToggle()
    }
  }, 100)
}

export function ResultsSwatches({ result, swatches }) {
  if (!swatches || swatches.length === 0) return null

  const filteredSwatches = swatches.filter((swatch) => !isSwatchOutOfStock(swatch))

  const handleOpenModal = () => {
    openSwatchModal(swatches)
  }

  return (
    <button
      type="button"
      className="flex w-fit flex-wrap items-center gap-x-0.25 gap-y-0.125 link-fade"
      onClick={handleOpenModal}
      aria-label="View all colours"
    >
      {filteredSwatches.slice(0, 3).map((swatch) => (
        <SwatchDot key={swatch.handle} swatch={swatch} isCurrentSwatch={swatch.handle === result.handle} />
      ))}
      {filteredSwatches.length > 3 && (
        <span className="paragraph-xs text-line text-neutral-secondary">+{filteredSwatches.length - 3} More</span>
      )}
    </button>
  )
}

function SwatchDot({ swatch, isCurrentSwatch }) {
  const colour = useMemo(() => {
    const tag = swatch?.tags?.find((tag) => tag.includes('Hex'))
    if (!tag) return 'var(--colour-accent)'
    return tag.split('-').pop()
  }, [swatch])

  const isLightColour = useMemo(() => {
    const hexColour = colour.toLowerCase()
    return !hexColour || hexColour === '#ffffff' || hexColour === '#fff' || hexColour === 'white'
  }, [colour])

  const criteria = useMemo(
    () => ({
      tags: swatch?.tags ?? []
    }),
    [swatch?.tags]
  )

  const altSwatch = useSwatches(criteria)

  return (
    <div
      className={clsx(
        'overflow-hidden flex size-1.375 shrink-0 rounded-full border-[4px] border-secondary bg-[--bg-colour] outline outline-1 outline-offset-0 outline-transparent transition-all',
        isCurrentSwatch ? '!outline-brand-black' : ''
      )}
      style={{
        '--bg-colour': altSwatch.length > 0 && altSwatch[0].background_colour ? altSwatch[0].background_colour : colour,
        ...(isLightColour && { boxShadow: 'inset 0px 0px 0px 1px var(--darkGrey)' })
      }}
    >
      {altSwatch.length > 0 && altSwatch[0].image && (
        <img src={altSwatch[0].image} alt="Product swatch" className="size-full object-cover" />
      )}
    </div>
  )
}
